interface ArrowOptions {
  color: string
  lineWidth?: number
  headLength?: number
  opacity?: number
}

interface PointOptions {
  color: string
  radius?: number
  opacity?: number
  selected?: boolean
  selectedStrokeColor?: string
}

interface DirectionLabelOptions {
  label?: string
  y?: number
  color?: string
  fontSize?: number
}

/**
 * Draw an arrow (line + arrowhead triangle) on the canvas.
 */
export function drawArrow(
  ctx: CanvasRenderingContext2D,
  from: { cx: number; cy: number },
  to: { cx: number; cy: number },
  options: ArrowOptions
) {
  const { color, lineWidth = 2, headLength = 12, opacity = 1 } = options

  ctx.save()
  ctx.globalAlpha = opacity
  ctx.strokeStyle = color
  ctx.lineWidth = lineWidth
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'

  // Draw line
  ctx.beginPath()
  ctx.moveTo(from.cx, from.cy)
  ctx.lineTo(to.cx, to.cy)
  ctx.stroke()

  // Draw arrowhead as an open stroked chevron (two lines back from the tip)
  // rather than a filled triangle — matches the designer spec.
  const angle = Math.atan2(to.cy - from.cy, to.cx - from.cx)
  ctx.beginPath()
  ctx.moveTo(
    to.cx - headLength * Math.cos(angle - Math.PI / 6),
    to.cy - headLength * Math.sin(angle - Math.PI / 6)
  )
  ctx.lineTo(to.cx, to.cy)
  ctx.lineTo(
    to.cx - headLength * Math.cos(angle + Math.PI / 6),
    to.cy - headLength * Math.sin(angle + Math.PI / 6)
  )
  ctx.stroke()

  ctx.restore()
}

/**
 * Draw a point (filled circle) on the canvas, with optional selection ring.
 */
export function drawPoint(
  ctx: CanvasRenderingContext2D,
  pos: { cx: number; cy: number },
  options: PointOptions
) {
  const {
    color,
    radius = 5,
    opacity = 1,
    selected = false,
    selectedStrokeColor = '#ffffff'
  } = options

  ctx.save()
  ctx.globalAlpha = opacity

  ctx.beginPath()
  ctx.arc(pos.cx, pos.cy, radius, 0, Math.PI * 2)
  ctx.fillStyle = color
  ctx.fill()

  if (selected) {
    ctx.beginPath()
    ctx.arc(pos.cx, pos.cy, radius + 2, 0, Math.PI * 2)
    ctx.strokeStyle = selectedStrokeColor
    ctx.lineWidth = 2
    ctx.stroke()
  }

  ctx.restore()
}

/**
 * Draw a direction label (e.g. "Attack →") centered at top of canvas.
 */
export function drawDirectionLabel(
  ctx: CanvasRenderingContext2D,
  canvasWidth: number,
  options?: DirectionLabelOptions
) {
  const {
    label = 'Attack →',
    y = 14,
    color = 'rgba(255, 255, 255, 0.5)',
    fontSize = 12
  } = options ?? {}

  ctx.save()
  ctx.font = `${fontSize}px sans-serif`
  ctx.fillStyle = color
  ctx.textAlign = 'center'
  ctx.textBaseline = 'top'
  ctx.fillText(label, canvasWidth / 2, y)
  ctx.restore()
}
